import React from 'react';
import styled from 'styled-components/native';
import {getByScreenSize, hdp, isLargeScreen, wdp} from '../../utils/responsive';
import TextGeneric from '../textGeneric';
import {Animated, Platform} from 'react-native';
import {OnboardingItemProps} from '../../interfaces/components/onboardingItem';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const OnBoardContainer = styled(Animated.View)`
  align-items: center;
`;
const ImageContainer = styled(Animated.View)`
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  overflow: hidden;
`;

const Image = styled.Image`
  width: 100%;
  height: 100%;
  ${Platform.OS !== 'web' && 'resize-mode: contain;'}
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.onboarding.text};
  font-size: ${({theme}) => theme.text.s5}px;
  width: 85%;
  text-align: ${Platform.OS === 'web' ? 'center' : 'left'};
`;

const Subtitle = styled(TextGeneric)`
  color: ${({theme}) => theme.onboarding.text + 'DD'};
  font-size: ${({theme}) => theme.text.s6}px;
  width: 85%;
  text-align: ${Platform.OS === 'web' ? 'center' : 'left'};
`;

/**
 * Represents a OnboardingItem component with animations.
 * @param {OnboardingItemProps} props - The props for the OnboardingItem component.
 * @returns {React.ReactElement} - Returns the JSX element for the OnboardingItem.
 */
const OnboardingItem: React.FC<OnboardingItemProps> = ({
  item,
  scrollX,
  index,
}: OnboardingItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {wdp, hdp, getByScreenSize, isLargeScreen, width} = useResponsiveDimensions();
  
  // Check if device is small (800px width and 500-700px height)
  const isSmallScreen = width <= 800;
  const screenHeight = require('react-native').Dimensions.get('window').height;
  const isLandscape = width > screenHeight;
  const isSmallLandscape = isSmallScreen && isLandscape;

  /**
   * Represents the range value for the scrollX animation.
   * @type {number}
   */
  const range = wdp(100);

  /**
   * Represents the input range for the scrollX animation.
   * @type {number[]}
   */
  const inputRange = [(index - 1) * range, index * range, (index + 1) * range];

  /**
   * Represents the interpolated opacity value for the scrollX animation.
   * @type {Animated.AnimatedInterpolation}
   */
  const opacity = scrollX.interpolate({
    inputRange,
    outputRange: [0, 1, 0],
  });

  /**
   * Represents the interpolated translateX value for the scrollX animation.
   * @type {Animated.AnimatedInterpolation}
   */
  const translateX = scrollX.interpolate({
    inputRange,
    outputRange: [250, 0, -250],
  });

  /**
   * Renders the OnboardingItem component.
   * @returns {React.ReactElement} - Returns the JSX element for the OnboardingItem.
   */
  return (
    <OnBoardContainer
      style={{
        opacity,
        width: wdp(100),
        paddingTop: isSmallLandscape ? hdp(5) : isSmallScreen ? hdp(8) : getByScreenSize(hdp(15), hdp(10)),
      }}>
      <ImageContainer
        style={{
          transform: [{translateX}],
          width: isSmallLandscape ? wdp(40) : isSmallScreen ? wdp(60) : getByScreenSize(wdp(75), wdp(30), wdp(50)),
          maxHeight: isSmallLandscape ? hdp(40) : isSmallScreen ? hdp(50) : getByScreenSize(222, hdp(70)),
          marginBottom: isSmallLandscape ? hdp(3) : isSmallScreen ? hdp(5) : getByScreenSize(hdp(10), hdp(5)),
        }}>
        <Image resizeMode={'contain'} source={item.image} />
      </ImageContainer>
      <Title style={{marginBottom: hdp(3)}} semiBold>
        {item.prevTitle && (
          <Title style={{color: theme.onboarding.textSecondary}}>
            {item.prevTitle}
          </Title>
        )}
        {item.title}
      </Title>
      <Subtitle style={{marginBottom: hdp(2)}}>{item.subtitle}</Subtitle>
    </OnBoardContainer>
  );
};

export default OnboardingItem;
